import { FlyoverPref, RotationMode, ZIndexes } from "../constants";
import { RectangleHitbox, type Hitbox } from "../utils/hitbox";
import { DefinitionType } from "../utils/objectDefinitions";
import { Vec, type Vector } from "../utils/vector";
import { type RawObstacleDefinition } from "./obstacles";

/*
    The roofs' ways down and up (1.08, the owner's picks on the roofs page, 29 October 2026; README "The roofs (1.08)"):
    a roof's hatch, a ladder's top on a roof's deck and its foot on the ground (and, until 0.98.2, the blocks' stair
    head door: their roofs are walked up onto since, `apt_roof_stair` in obstacles.ts). F takes
    whoever is by one through it (`passage`, Player.takePassage), out at its way out on the floor it leads to (the
    nearest clear spot to it there), after a moment's climb (`PASSAGE_CLIMB_MS`: 1.09). Each is turned with its
    building, a ladder as its wall faces: drawn with the wall at the top of its picture, the ladder standing out from it
    towards the bottom (rotation 0: on a wall facing south).
    Only a ladder's foot and the ladder under a hatch are drawn as themselves (obstacles/roofs/, tools/fargo/roofs.py);
    a hatch and a ladder's top are drawn with the roof they're in (its building's pictures, buildings/roofs/), the
    passages themselves unseen.

    1.09 (the owner, 29 October 2026: "The problem with one sided hatches is that a player can stay there and not die,
    thats an issue, them being visible and reachable is very important, so on the other side of the hatch there should
    either a ladder that the player slides down or up"): every hatch has a ladder under it on the floor below
    (`hatch_ladder`), F at it climbing up through the hatch onto the deck; the blocks' stairs went up again from their
    landing (`roof_stair_up`, until 0.98.2's flight up). No roof is a one-way place any more.
*/

/** how long a climb takes (ms): through a hatch, up or down a ladder (Player.takePassage) */
export const PASSAGE_CLIMB_MS = 650;
/**
 * the blocks' long ladder (0.98.1, the owner, 30 October 2026: "add a ladder but it takes 8 seconds to climb al lthe
 * way down/up, animate it well"): up a block's end wall from the ground to its roof, a climb of `BLOCK_LADDER_MS` up or
 * down, seen all the way (`passage.climbMs`, `passage.ladder`: Player.takePassage)
 */
export const BLOCK_LADDER_MS = 8000;
/**
 * the long ladder's places, from the wall's outer face: its foot's middle out from it (its picture 3 deep, its top
 * against the wall), where its climber hangs on it (out from it: their back to the open, their front to the wall), its
 * top's middle in from it (on the deck, by the parapet), and the ways off it: onto the deck (in from it) and onto the
 * ground (out from it)
 */
export const BLOCK_LADDER = Object.freeze({ foot: 1.5, hang: 2.3, top: 5, onDeck: 7, offGround: 5.5 });

/** a way between floors (1.08): walked over, never hit, its prompt its own (`interact_<id>`, or `prompt`'s) */
const passage = (
    idString: string,
    name: string,
    hitbox: Hitbox,
    layers: number,
    exit: Vector,
    options: Partial<RawObstacleDefinition> = {}
): RawObstacleDefinition => ({
    idString,
    name,
    defType: DefinitionType.Obstacle,
    material: "metal_light",
    health: 1000,
    indestructible: true,
    noResidue: true,
    hideOnMap: true,
    noCollisions: true,
    noBulletCollision: true,
    noMeleeCollision: true,
    allowFlyover: FlyoverPref.Always,
    rotationMode: RotationMode.Limited,
    customInteractMessage: true,
    hitbox,
    passage: { layers, exit },
    // (nothing hits one; its particle its material's all the same, as every piece's: 0.98.1)
    frames: { particle: options.material === "wood" ? "wood_particle" : "metal_particle" },
    ...options
} as RawObstacleDefinition);

/** how far a ladder's top on a roof's deck lets you out beyond the wall (past its foot), and its foot in onto the deck */
export const LADDER_DOWN = 7.5;
export const LADDER_UP = 8;
/**
 * down a hatch, you come out this far past the foot of the ladder under it (1.09: beside the ladder, not standing on
 * its picture: it's there to be seen and climbed again; `HATCH_LADDER` in tools/fargo/roofs.py is 4.4 across)
 */
export const HATCH_OUT = 4.6;

export const ROOF_OBSTACLES: readonly RawObstacleDefinition[] = [
    // a hatch in a roof's deck: down to the floor under it, out at the foot of the ladder under it
    passage("roof_hatch", "Roof Hatch", RectangleHitbox.fromRect(6, 6), -2, Vec(0, HATCH_OUT), { invisible: true }),
    // the cell block's wooden ones (its roof's own picture of one over the guard hall, 12 by 12, and the two more)
    passage("prison_roof_hatch", "Roof Hatch", RectangleHitbox.fromRect(8, 8), -2, Vec(0, HATCH_OUT), {
        invisible: true,
        interactObstacleIdString: "roof_hatch",
        // (wood, as it's drawn: and nothing of the prison's sounds as the barrels' do, prison.test.ts)
        material: "wood"
    }),
    // the ladder under a hatch, on the floor below (1.09): up it, through the hatch, onto the deck right over it (0.98.1:
    // a loft ladder let down from the hatch, its top along its picture's top, turned with the hatch: by a wall, its top
    // against it)
    // (its F zone on past its feet, towards the way out of the hatch over it: from there you can go straight back up,
    // a ladder turned to a wall and stood in from its hatch by a parapet too)
    passage("hatch_ladder", "Ladder", RectangleHitbox.fromRect(4.4, 5.6, Vec(0, 0.6)), 2, Vec(0, 0), {
        zIndex: ZIndexes.ObstaclesLayer1,
        interactObstacleIdString: "roof_ladder"
    }),
    // a ladder's top over a roof's edge: down it, out at its foot on the ground
    passage("roof_ladder_top", "Ladder", RectangleHitbox.fromRect(4.4, 4), -2, Vec(0, LADDER_DOWN), { invisible: true }),
    // its foot against the wall: up it, onto the deck
    passage("roof_ladder", "Ladder", RectangleHitbox.fromRect(4.4, 3), 2, Vec(0, -LADDER_UP), {
        zIndex: ZIndexes.ObstaclesLayer1
    }),
    // the blocks' long ladder (0.98.1): its foot on the ground against the end wall (the wall at its picture's top, as
    // a ladder's foot), up it onto the roof, 8 s ...
    passage("block_ladder", "Ladder", RectangleHitbox.fromRect(4.4, 3), 8, Vec(0, -(BLOCK_LADDER.foot + BLOCK_LADDER.onDeck)), {
        zIndex: ZIndexes.ObstaclesLayer1,
        interactObstacleIdString: "roof_ladder",
        passage: {
            // (the ground to the blocks' roof, `APARTMENT_DECK`'s layer: four floors and their stairs' layers)
            layers: 8,
            exit: Vec(0, -(BLOCK_LADDER.foot + BLOCK_LADDER.onDeck)),
            climbMs: BLOCK_LADDER_MS,
            ladder: Vec(0, BLOCK_LADDER.hang - BLOCK_LADDER.foot)
        }
    }),
    // ... and its top over the roof's parapet (the roof at its picture's top, as a ladder's top), down it to the ground
    passage("block_ladder_top", "Ladder", RectangleHitbox.fromRect(4.4, 4), -8, Vec(0, BLOCK_LADDER.top + BLOCK_LADDER.offGround), {
        invisible: true,
        interactObstacleIdString: "roof_ladder_top",
        passage: {
            layers: -8,
            exit: Vec(0, BLOCK_LADDER.top + BLOCK_LADDER.offGround),
            climbMs: BLOCK_LADDER_MS,
            ladder: Vec(0, BLOCK_LADDER.top + BLOCK_LADDER.hang)
        }
    })
];
